import { toValue, type MaybeRefOrGetter, type Ref } from "vue";
import * as Sentry from "@sentry/vue";
import type { MemberSpeckleStatus, UserInProjectType } from "@/features/BuildingProject";
import { getOwnSpeckleStreamAccess, getSpeckleMemberAccess } from "@features/Speckle";
import { useSpeckleUser } from "@/stores/speckle/speckleUser.store";

/** What to say after an invite that did not reach the project's Speckle model; null when nothing is wrong or known. */
export const speckleInviteWarning = (status: MemberSpeckleStatus): string | null => {
  if (status === "noAccount") {
    return "No Speckle account yet, so they can't open the project's 3D model or publish to it. They'll be asked to set up Speckle when they open the project.";
  }
  if (status === "notOnModel") {
    return "Not yet added to the project's 3D model. They'll be offered \"Sync projects\" when they open the project.";
  }
  return null;
};

// Each member's standing with the stream, read from Speckle with the viewer's token and written onto the
// rows as `speckleStatus`. The list never waits on it, and anything unknown stays null.
export const useMemberSpeckleStatus = (
  streamId: MaybeRefOrGetter<string | null | undefined>,
  members: Ref<UserInProjectType[]>,
  selfUserId: MaybeRefOrGetter<string | null | undefined>
) => {
  // A slower, older refresh must not overwrite a newer one's answer.
  let generation = 0;

  // The viewer's own Speckle account may sit under another email, so their row uses their own link.
  const ownStatus = async (stream: string): Promise<MemberSpeckleStatus> => {
    const speckleUser = useSpeckleUser();
    await speckleUser.ensureUserStatus();
    if (speckleUser.hasStatusError) return null;
    if (!speckleUser.isLinked) return "noAccount";
    const access = await getOwnSpeckleStreamAccess(stream);
    return access === "readable" ? "onModel" : access === "refused" ? "notOnModel" : null;
  };

  const setStatus = (userId: string, status: MemberSpeckleStatus) => {
    const row = members.value.find((member) => member.userId === userId);
    if (row) row.speckleStatus = status;
  };

  const lookUp = async (stream: string, targets: UserInProjectType[]) => {
    const self = toValue(selfUserId);
    const others = targets.filter((member) => member.userId !== self);
    const [own, access] = await Promise.all([
      targets.some((member) => member.userId === self) ? ownStatus(stream) : Promise.resolve(null),
      others.length > 0 ? getSpeckleMemberAccess(stream, others.map((member) => member.email)) : Promise.resolve(null),
    ]);
    return targets.map((member) => ({
      userId: member.userId,
      status: member.userId === self ? own : (access?.get(member.email.trim().toLowerCase()) ?? null),
    }));
  };

  const refresh = async (): Promise<void> => {
    const current = ++generation;
    const stream = toValue(streamId);
    const targets = [...members.value];
    if (!stream) {
      targets.forEach((member) => setStatus(member.userId, null));
      return;
    }
    try {
      const results = await lookUp(stream, targets);
      if (current !== generation) return;
      results.forEach(({ userId, status }) => setStatus(userId, status));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const checkOne = async (member: UserInProjectType): Promise<MemberSpeckleStatus> => {
    const stream = toValue(streamId);
    if (!stream) return null;
    try {
      const [result] = await lookUp(stream, [member]);
      setStatus(member.userId, result.status);
      return result.status;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  };

  return { refresh, checkOne };
};
